import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment as env } from '../../environments/environment';

/**
 * IT-F2-426 c/2795820a (Mike 2026-10-02). Left-menu "Agreement Review"
 * badge count. Hits the lightweight
 * `/rest/user/data-agreements/admin/review-pending-count` endpoint on
 * boot + every POLL_MS; also exposes `bump()` so the review page can
 * refresh after an approve / decline / bulk op mutates the queue
 * without waiting for the next poll tick.
 */
@Injectable({ providedIn: 'root' })
export class AgreementReviewBadgeService implements OnDestroy {
  private readonly _subject = new BehaviorSubject<number>(0);
  readonly pending$: Observable<number> = this._subject.asObservable();
  private _timer: any = null;
  private static readonly POLL_MS = 60_000;

  constructor(private http: HttpClient) {}

  start() {
    if (this._timer) return;
    this.refresh();
    this._timer = setInterval(() => this.refresh(), AgreementReviewBadgeService.POLL_MS);
  }

  stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  ngOnDestroy() {
    this.stop();
  }

  async refresh(): Promise<void> {
    try {
      const res: any = await this.http.get(
        `${env.apiUrl}/rest/user/data-agreements/admin/review-pending-count`,
        { withCredentials: true },
      ).toPromise();
      const n = Number(res?.pending || 0);
      this._subject.next(Number.isFinite(n) && n >= 0 ? n : 0);
    } catch {
      // Silent — don't spam toasts for a background poll. Next tick retries.
    }
  }

  /** Review page calls this after any decision write + bulk op so the
   *  menu badge updates without a 60s wait. */
  bump() { this.refresh(); }

  /** Last known value — for synchronous reads where an Observable would
   *  pull in extra template plumbing. */
  snapshot(): number { return this._subject.value; }

  /** `"9+"` for counts ≥ 10, otherwise the stringified count (or empty
   *  for 0). Matches the "up to 9+" presentation Mike asked for. */
  static format(n: number): string {
    if (!n || n <= 0) return '';
    return n >= 10 ? '9+' : String(n);
  }
}
